<!--
  TenantPicker — selector de tenant activo de Otera (patrón combobox +
  listbox ARIA). Reemplaza a TenantPickerLegacy. Se oculta si hay ≤ 1 tenant.
-->

<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, ref, useId } from 'vue';

import Icon from './Icon.vue';

defineOptions({ name: 'DsTenantPicker' });

const props = defineProps({
  tenants: {
    type: Array,
    required: true,
    validator: (v) => Array.isArray(v) && v.every((t) => t && typeof t.app_id === 'string')
  },
  activeAppId: { type: String, default: null },
  label: { type: String, default: 'Active tenant' }
});

const emit = defineEmits(['select']);

const fallbackId = useId();
const buttonId = computed(() => `tenant-picker-${fallbackId}`);
const listboxId = computed(() => `tenant-picker-${fallbackId}-listbox`);

const open = ref(false);
const focusIndex = ref(-1);
const root = ref(null);
const buttonRef = ref(null);
const listboxRef = ref(null);

const activeTenant = computed(
  () => props.tenants.find((t) => t.app_id === props.activeAppId) ?? props.tenants[0] ?? null
);
const activeId = computed(() =>
  focusIndex.value >= 0 ? `${listboxId.value}-opt-${focusIndex.value}` : null
);

function tenantLabel(t) { return t.name || t.app_id; }

function openMenu() {
  if (props.tenants.length <= 1) return;
  open.value = true;
  const idx = props.tenants.findIndex((t) => t.app_id === props.activeAppId);
  focusIndex.value = idx >= 0 ? idx : 0;
  nextTick(() => listboxRef.value?.focus());
}

function closeMenu({ returnFocus = true } = {}) {
  open.value = false;
  focusIndex.value = -1;
  if (returnFocus) nextTick(() => buttonRef.value?.focus());
}

function selectIndex(idx) {
  const t = props.tenants[idx];
  if (!t) return;
  if (t.app_id !== props.activeAppId) emit('select', t.app_id);
  closeMenu({ returnFocus: true });
}

function onButtonKeydown(event) {
  if (event.key === 'ArrowDown' || event.key === 'Enter' || event.key === ' ') {
    event.preventDefault();
    openMenu();
  }
}

function onListboxKeydown(event) {
  const last = props.tenants.length - 1;
  switch (event.key) {
    case 'ArrowDown':
      event.preventDefault();
      focusIndex.value = focusIndex.value < last ? focusIndex.value + 1 : 0;
      break;
    case 'ArrowUp':
      event.preventDefault();
      focusIndex.value = focusIndex.value > 0 ? focusIndex.value - 1 : last;
      break;
    case 'Home':
      event.preventDefault();
      focusIndex.value = 0;
      break;
    case 'End':
      event.preventDefault();
      focusIndex.value = last;
      break;
    case 'Enter':
    case ' ':
      event.preventDefault();
      selectIndex(focusIndex.value);
      break;
    case 'Escape':
      event.preventDefault();
      closeMenu();
      break;
    case 'Tab':
      closeMenu({ returnFocus: false });
      break;
  }
}

function onDocumentClick(event) {
  if (!open.value) return;
  if (!root.value?.contains(event.target)) closeMenu({ returnFocus: false });
}

onMounted(() => document.addEventListener('mousedown', onDocumentClick));
onBeforeUnmount(() => document.removeEventListener('mousedown', onDocumentClick));
</script>

<template>
  <div
    v-if="tenants.length > 1"
    ref="root"
    class="tp"
  >
    <button
      :id="buttonId"
      ref="buttonRef"
      type="button"
      class="tp__trigger"
      role="combobox"
      :aria-expanded="open"
      :aria-controls="listboxId"
      :aria-label="label"
      aria-haspopup="listbox"
      @click="open ? closeMenu() : openMenu()"
      @keydown="onButtonKeydown"
    >
      <span class="tp__text">{{ activeTenant ? tenantLabel(activeTenant) : '—' }}</span>
      <Icon
        name="chevDown"
        :size="13"
        :class="['tp__chev', { 'tp__chev--open': open }]"
      />
    </button>

    <ul
      v-show="open"
      :id="listboxId"
      ref="listboxRef"
      class="tp__listbox"
      role="listbox"
      tabindex="-1"
      :aria-label="label"
      :aria-activedescendant="activeId || undefined"
      @keydown="onListboxKeydown"
    >
      <!-- eslint-disable-next-line vuejs-accessibility/click-events-have-key-events -->
      <li
        v-for="(t, idx) in tenants"
        :id="`${listboxId}-opt-${idx}`"
        :key="t.app_id"
        role="option"
        :class="[
          'tp__option',
          { 'tp__option--focused': idx === focusIndex, 'tp__option--active': t.app_id === activeAppId }
        ]"
        :aria-selected="t.app_id === activeAppId"
        @click="selectIndex(idx)"
        @mousemove="focusIndex = idx"
      >
        <Icon
          v-if="t.app_id === activeAppId"
          name="check"
          :size="12"
          :stroke="2.5"
          class="tp__check"
        />
        <span class="tp__option-label">{{ tenantLabel(t) }}</span>
      </li>
    </ul>
  </div>
</template>

<style scoped>
.tp {
  position: relative;
  display: inline-flex;
}

.tp__trigger {
  display: inline-flex;
  align-items: center;
  gap: var(--space-2);
  height: var(--control-sm);
  padding: 0 var(--space-3);
  border-radius: var(--radius-md);
  background: var(--bg-canvas);
  color: var(--fg-primary);
  border: var(--bw-thin) solid var(--border-default);
  font-size: var(--text-sm);
  font-weight: var(--fw-medium);
  cursor: pointer;
}

.tp__trigger:hover {
  border-color: var(--border-strong);
}

.tp__chev {
  color: var(--fg-tertiary);
  transition: transform var(--motion-fast) var(--motion-ease-out);
}

.tp__chev--open {
  transform: rotate(180deg);
}

.tp__listbox {
  position: absolute;
  top: calc(100% + var(--space-1));
  right: 0;
  min-width: 14rem;
  max-height: 16rem;
  overflow-y: auto;
  margin: 0;
  padding: var(--space-1);
  background: var(--bg-canvas);
  border: var(--bw-thin) solid var(--border-default);
  border-radius: var(--radius-md);
  box-shadow: var(--shadow-lg);
  z-index: var(--z-overlay);
  list-style: none;
}

.tp__option {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  padding: var(--space-2) var(--space-3);
  border-radius: var(--radius-sm);
  cursor: pointer;
  font-size: var(--text-sm);
  color: var(--fg-primary);
}

.tp__option--focused {
  background: var(--bg-hover);
}

.tp__option-label {
  flex: 1;
}

.tp__option--active .tp__option-label {
  color: var(--accent-fg-on-soft);
  font-weight: var(--fw-medium);
}

.tp__check {
  color: var(--accent-fg-on-soft);
  flex-shrink: 0;
}

</style>
